import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  InputAdornment,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreIcon from '@mui/icons-material/Restore';
import DiscountIcon from '@mui/icons-material/Discount';

import { Spinner } from '../..';
import messages from '../../../static/messages';
import discountsServices from '../../../services/discountsServices';
import { button } from '../../../static/theme-styles';
import { actions_column, table_container_base } from '../../../static/styles';

const DiscountsTable = () => {
  const [items, setItems] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ 
    description: '', 
    amount: 0, 
    defaultExpirationDays: 30,
    isActive: true 
  });
  const [showDeleted, setShowDeleted] = useState(false);
  const confirm = useConfirm();

  const loadItems = async () => {
    try {
      const response = await discountsServices.getAll({ showDeleted, size: 1000 });
      const data = response.data?.data || response.data || [];
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  useEffect(() => {
    loadItems();
  }, [showDeleted]);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ 
        description: item.description, 
        amount: item.amount || 0,
        defaultExpirationDays: item.defaultExpirationDays || 30,
        isActive: item.isActive !== false
      });
    } else {
      setEditingItem(null);
      setFormData({ 
        description: '', 
        amount: 0, 
        defaultExpirationDays: 30,
        isActive: true 
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormData({ 
      description: '', 
      amount: 0, 
      defaultExpirationDays: 30,
      isActive: true 
    });
  };

  const handleSubmit = async () => {
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }

    try {
      if (editingItem) {
        const response = await discountsServices.update(editingItem.id, {
          description: formData.description.trim(),
          amount: Number(formData.amount) || 0,
          defaultExpirationDays: Number(formData.defaultExpirationDays) || 30,
          isActive: formData.isActive
        });
        if (response.status === 200) {
          toast.success('Discount updated');
          handleCloseDialog();
          loadItems();
        }
      } else {
        const response = await discountsServices.create({
          description: formData.description.trim(),
          amount: Number(formData.amount) || 0,
          defaultExpirationDays: Number(formData.defaultExpirationDays) || 30,
          isActive: formData.isActive
        });
        if (response.status === 201) {
          toast.success('Discount created');
          handleCloseDialog();
          loadItems();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || messages.errors.error_try_again);
    }
  };

  const handleDelete = (id) => {
    confirm({
      title: messages.confirm_message.remove,
      description: 'This will delete the discount from the catalog.',
    })
      .then(async () => {
        try {
          await discountsServices.remove(id);
          toast.success('Discount deleted');
          loadItems();
        } catch (error) {
          toast.error(error.response?.data?.message || messages.errors.error_try_again);
        }
      })
      .catch(() => {});
  };

  const handleRestore = async (id) => {
    try {
      await discountsServices.restore(id);
      toast.success('Discount restored');
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.message || messages.errors.error_try_again);
    }
  };

  if (!items) return <Spinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DiscountIcon color="primary" />
          Discounts Catalog
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                size="small"
              />
            }
            label="Show deleted"
          />
          <Button
            variant="contained"
            endIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={button('primary', 'secondary')}
          >
            Add New
          </Button>
        </Box>
      </Box>

      <Paper>
        <TableContainer sx={table_container_base}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell width="120px">Amount</TableCell>
                <TableCell width="150px">Default Expiration</TableCell>
                <TableCell width="100px">Status</TableCell>
                <TableCell width="120px">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length ? (
                items.map((item) => (
                  <TableRow 
                    key={item.id} 
                    hover
                    sx={{ 
                      opacity: item.deletedAt ? 0.5 : 1,
                      bgcolor: item.deletedAt ? 'action.hover' : 'inherit'
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      {item.description}
                      {item.deletedAt && (
                        <Chip 
                          label="Deleted" 
                          size="small" 
                          color="error" 
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, color: 'success.main' }}>
                        ${item.amount?.toFixed(2) || '0.00'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.defaultExpirationDays} days
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.isActive ? 'Active' : 'Inactive'} 
                        size="small" 
                        color={item.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell sx={actions_column}>
                      {item.deletedAt ? (
                        <Tooltip title="Restore">
                          <IconButton onClick={() => handleRestore(item.id)} color="primary">
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <>
                          <Tooltip title="Edit">
                            <IconButton onClick={() => handleOpenDialog(item)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton onClick={() => handleDelete(item.id)}>
                              <DeleteForeverIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No discounts in catalog yet</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Discount' : 'Add New Discount'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Description"
            fullWidth
            required
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
            placeholder="e.g., Yelp Discount, Holiday Special"
          />
          <TextField
            margin="dense"
            label="Amount"
            type="number"
            fullWidth
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
          <TextField
            margin="dense"
            label="Default Expiration"
            type="number"
            fullWidth
            value={formData.defaultExpirationDays}
            onChange={(e) => setFormData({ ...formData, defaultExpirationDays: e.target.value })}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">days</InputAdornment>,
            }}
            helperText="Number of days from today when discount expires by default"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="Active (show in selection list)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DiscountsTable;
